import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { ApiResponse } from "apisauce";
import { toast } from "react-toastify";

import { RoyaltyConfig } from "../../../interface/api/RoyaltyConfig";
import royaltyService from "../../../services/royaltyService";
import { calcPreview, calcRoyalty } from "../../../utils/calcRoyalty";
import { formatCurrency } from "../../../utils/parser";
import ReportTerms from "../report-terms";
import { StatusColors } from "../../../utils/constants";

// Sizes track the theme's type scale; only the monospace blocks, which have to line up
// column by column, set a family of their own.
const useStyles = makeStyles((theme) => ({
  divider: {
    margin: theme.spacing(0.5, 0, 1.25),
  },
  formula: {
    fontFamily: "monospace",
    fontSize: theme.typography.caption.fontSize as string,
    lineHeight: 1.7,
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    overflowX: "auto",
  },
  section: {
    marginTop: theme.spacing(2),
  },
  cell: {
    fontSize: theme.typography.caption.fontSize as string,
    padding: theme.spacing(0.75, 1),
  },
  step: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.25, 0),
  },
  stepValue: {
    fontFamily: "monospace",
    fontWeight: theme.typography.fontWeightBold as number,
    whiteSpace: "nowrap",
  },
}));

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.section}>
      <Typography variant="overline" color="textSecondary">
        {title}
      </Typography>
      <Divider className={classes.divider} />
      {children}
    </Box>
  );
};

/**
 * How the royalty report arrives at its numbers, with this portal's live configuration
 * substituted in and a worked example anyone can drive. The math is agreed with partners
 * and then argued about a year later; a total nobody can reproduce by hand is a total
 * nobody can defend.
 */
const RoyaltyMethodPanel = () => {
  const classes = useStyles();

  const [config, setConfig] = useState<RoyaltyConfig | null>(null);
  const [exampleCpi, setExampleCpi] = useState("188.7");
  const [exampleTownRate, setExampleTownRate] = useState("13.60");
  const [examplePrice, setExamplePrice] = useState("95.00");

  useEffect(() => {
    const loadConfig = async () => {
      const response: ApiResponse<any> = await royaltyService.getConfig();
      if (response.ok && response.data) {
        setConfig(response.data);
      } else {
        toast.error("Failed to load royalty configuration");
      }
    };
    loadConfig();
  }, []);

  const cpi = parseFloat(exampleCpi);
  const town = parseFloat(exampleTownRate);
  const price = parseFloat(examplePrice);

  const worked = useMemo(() => {
    if (
      !config ||
      Number.isNaN(cpi) ||
      Number.isNaN(town) ||
      Number.isNaN(price)
    ) {
      return null;
    }

    const preview = calcPreview(cpi, town, config);
    return {
      preview,
      difference: price - preview.adjustedTicketPrice,
      // Same mirror of the server math the live form preview uses, so the worked
      // example cannot say one thing while the report does another.
      royalties: config.partners.map((partner) => ({
        partner,
        royalty: calcRoyalty(price, preview.adjustedTicketPrice, partner),
      })),
    };
  }, [config, cpi, town, price]);

  if (!config) {
    return (
      <Typography variant="body2" color="textSecondary">
        Loading the royalty configuration...
      </Typography>
    );
  }

  return (
    <div>
      <Section title="1 — The adjusted ticket price">
        <Typography variant="body2" color="textSecondary" paragraph>
          The floor every selling price is measured against. It moves the base rate
          by the change in CPI since the background CPI, then adds however much the
          town royalty rate has moved since its own base.
        </Typography>
        <div className={classes.formula}>
          adjusted = baseRate × (1 + (CPI − backgroundCPI) ÷ backgroundCPI)
          <br />
          {"           "}+ (townRate − townBaseRate)
          <br />
          <br />
          baseRate = {formatCurrency(config.baseRate)}
          {"   "}backgroundCPI = {config.backgroundCpi}
          {"   "}townBaseRate = {formatCurrency(config.townBaseRate)}
        </div>
      </Section>

      <Section title="2 — Royalty owed on one ticket">
        <Typography variant="body2" color="textSecondary" paragraph>
          The partner takes their base royalty plus their share of the difference
          between the selling price and that floor. A deficit subtracts, with no
          floor, so a line can come out below $0.00 — unless the partner is exempt
          from deficits, in which case they keep the base royalty.
        </Typography>
        <div className={classes.formula}>
          difference = sellingPrice − adjusted
          <br />
          <br />
          surplus (difference ≥ 0){"  "}→ baseRoyalty + difference × rate
          <br />
          deficit{"  "}(applies){"     "}→ baseRoyalty + difference × rate
          <br />
          deficit{"  "}(exempt){"      "}→ baseRoyalty
        </div>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.section}
        >
          Hydrovac and Small Quantities are calculated on the net ticket price of{" "}
          {formatCurrency(config.netTicketPrice)} in place of the selling price, for
          every ticket in those streams. Main Pit and Rubble use each ticket&apos;s own
          selling price. The net ticket price is set in Settings and can be overridden
          for a single run.
        </Typography>
      </Section>

      <Section title="3 — Partners">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Partner</TableCell>
              <TableCell className={classes.cell}>Share</TableCell>
              <TableCell className={classes.cell}>Base royalty</TableCell>
              <TableCell className={classes.cell}>Deficits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {config.partners.map((partner) => (
              <TableRow key={partner.key}>
                <TableCell className={classes.cell}>{partner.name}</TableCell>
                <TableCell className={classes.cell}>
                  {(partner.rate * 100).toFixed(0)}%
                </TableCell>
                <TableCell className={classes.cell}>
                  {formatCurrency(partner.baseRoyalty)}
                </TableCell>
                <TableCell className={classes.cell}>
                  {partner.applyDeficit ? "Apply" : "Exempt"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="4 — Which tickets the total is built from">
        <Typography variant="body2" color="textSecondary">
          A ticket settles only if it was received <b>and</b> written up for a
          customer. Pre-generated blanks can be marked received without ever being
          used, so those are skipped and counted, never billed. A ticket that is not
          marked received is not considered at all, even if it still carries a
          received date from before it was un-received. Of the tickets
          that do settle, only ones with a recorded selling price make up the royalty
          total; unpriced ones are listed at $0.00 in no total, or — when you ask for
          it — valued at the average selling price of the priced tickets in the same
          period and totalled separately.
        </Typography>
      </Section>

      <Section title="5 — Worked example">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="CPI"
              type="number"
              value={exampleCpi}
              onChange={(e) => setExampleCpi(e.target.value)}
              variant="outlined"
              inputProps={{ step: "0.1" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Town rate ($)"
              type="number"
              value={exampleTownRate}
              onChange={(e) => setExampleTownRate(e.target.value)}
              variant="outlined"
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Selling price ($)"
              type="number"
              value={examplePrice}
              onChange={(e) => setExamplePrice(e.target.value)}
              variant="outlined"
              inputProps={{ step: "0.01" }}
            />
          </Grid>
        </Grid>

        {worked && (
          <Box mt={1.5}>
            <Typography variant="body2" component="div" className={classes.step}>
              <span>CPI increase</span>
              <span className={classes.stepValue}>
                {(worked.preview.cpiIncrease * 100).toFixed(2)}%
              </span>
            </Typography>
            <Typography variant="body2" component="div" className={classes.step}>
              <span>Adjusted base rate</span>
              <span className={classes.stepValue}>
                {formatCurrency(worked.preview.adjustedBaseRate)}
              </span>
            </Typography>
            <Typography variant="body2" component="div" className={classes.step}>
              <span>Town adjustment</span>
              <span className={classes.stepValue}>
                {formatCurrency(worked.preview.townAdjustment)}
              </span>
            </Typography>
            <Typography variant="body2" component="div" className={classes.step}>
              <span>
                <b>Adjusted ticket price</b>
              </span>
              <span className={classes.stepValue}>
                <b>{formatCurrency(worked.preview.adjustedTicketPrice)}</b>
              </span>
            </Typography>
            <Typography variant="body2" component="div" className={classes.step}>
              <span>Difference on a {formatCurrency(price)} ticket</span>
              <span
                className={classes.stepValue}
                style={{
                  color:
                    worked.difference >= 0
                      ? StatusColors.positive
                      : StatusColors.negative,
                }}
              >
                {formatCurrency(worked.difference)}
              </span>
            </Typography>

            <Divider className={classes.divider} />

            {worked.royalties.map(({ partner, royalty }) => (
              <Typography
                variant="body2"
                component="div"
                className={classes.step}
                key={partner.key}
              >
                <span>
                  {partner.name} — {formatCurrency(partner.baseRoyalty)} +{" "}
                  {(partner.rate * 100).toFixed(0)}% ×{" "}
                  {formatCurrency(worked.difference)}
                  {!partner.applyDeficit && worked.difference < 0
                    ? " (deficit-exempt)"
                    : ""}
                </span>
                <span
                  className={classes.stepValue}
                  style={{
                    color:
                      royalty >= 0
                        ? StatusColors.positive
                        : StatusColors.negative,
                  }}
                >
                  {formatCurrency(royalty)}
                </span>
              </Typography>
            ))}
          </Box>
        )}
      </Section>

      <Section title="6 — Definitions">
        <ReportTerms terms={config.terms ?? []} />
      </Section>
    </div>
  );
};

export default RoyaltyMethodPanel;
